import {Icon, Stroke, Style} from 'ol/style.js';
import type {Options as VectorSourceOptions} from 'ol/source/Vector';
import VectorSource from 'ol/source/Vector.js';
import FormatGeoJSON from 'ol/format/GeoJSON.js';
import {proj as epsg21781} from '@geoblocks/proj/src/EPSG_21781.js';
import type {Options as LayerGroupOptions} from 'ol/layer/Group';
import LayerGroup from 'ol/layer/Group.js';
import VectorLayer from 'ol/layer/Vector.js';
import {Z_INDEXES} from '../constants.ts';
import {getCached} from '../cache-helper.ts';
import {BASE_API_URL} from '../../envs.ts';
import type Feature from 'ol/Feature';
import type {FeatureLike} from 'ol/Feature';

const getIconSrc = (type: string, highlight: boolean = false) => {
  return type
    ? new Style({
        image: new Icon({
          src: `/img/detour/${type}${highlight ? '_h' : ''}.svg?ts11=TIMESTAMP11`,
          anchor: [0.5, 0.5],
        }),
      })
    : null;
};

export const getDetourIconStyle = (
  feature: FeatureLike,
  highlight: boolean = false,
) => {
  const detourAttrs: any = feature.getProperties();
  if (detourAttrs.symbol_intermediate && detourAttrs.is_intermediate_point) {
    return getIconSrc(detourAttrs.symbol_intermediate, highlight);
  }
  if (detourAttrs.symbol_start_end && detourAttrs.is_start_end_point) {
    return getIconSrc(detourAttrs.symbol_start_end, highlight);
  }
  return null;
};

export class DetourSource extends VectorSource {
  constructor(land: string, options?: VectorSourceOptions<Feature>) {
    super(
      Object.assign(
        {},
        {
          format: new FormatGeoJSON({
            dataProjection: epsg21781,
          }),
          url: `${BASE_API_URL}/api/4/goodtoknow/detours?land=${land}`,
        },
        options,
      ),
    );
  }
}

export class DetourLayerGroup extends LayerGroup {
  private lineStyleCache: {[key: string]: Style | Style[]} = {};

  constructor(
    land: string,
    name: string,
    options?: LayerGroupOptions,
    source?: VectorSource,
  ) {
    source =
      source || getCached(`detours_${land}`, () => new DetourSource(land));
    super(
      Object.assign(
        {},
        {
          visible: false,
          layers: [
            new VectorLayer({
              zIndex: Z_INDEXES.DETOURS_POINT,
              style: (feature) => getDetourIconStyle(feature),
              source: source,
              maxResolution: options?.maxResolution || 20,
            }),
            new VectorLayer({
              zIndex: Z_INDEXES.DETOURS_LINE,
              style: (feature, resolution) => {
                return this.getLineStyle(
                  feature.get('is_closed_way'),
                  resolution,
                );
              },
              source: source,
              opacity: 0.8,
              maxResolution: options?.maxResolution || 20,
            }),
          ],
          properties: {name: `detour_${name}`},
        },
        options,
      ),
    );
  }

  getLineStyle(closedWay: boolean, resolution: number) {
    const width = resolution > 5 ? 10 : 12;
    const key = `${width}_${closedWay}`;
    let style = this.lineStyleCache[key];
    if (!style) {
      if (closedWay) {
        style = [
          new Style({
            stroke: new Stroke({
              width: width,
              color: [255, 255, 255],
            }),
          }),
          new Style({
            stroke: new Stroke({
              width: width,
              color: [255, 0, 0],
              lineDash: [10, 30],
              lineCap: 'square',
              lineJoin: 'miter',
              miterLimit: 10,
            }),
          }),
        ];
      } else {
        style = [
          new Style({
            stroke: new Stroke({
              width: width,
              color: [249, 181, 0],
            }),
          }),
        ];
      }
      this.lineStyleCache[key] = style;
    }
    return style;
  }
}
